"use client";

import React, { ReactNode, useEffect, useRef, useState } from "react";
import { Link } from "@cappelendamm/design-system/react";
import classNames from "classnames";
import { useAccordion } from "../../components/useAccordion";

interface ExpandableTextProps {
  maxLines?: number;
  children?: ReactNode;
  truncated?: ReactNode;
  isFullWidth?: boolean;
  expandLabel?: string;
  collapseLabel?: string;
  wrapperClassName?: string;
  contentClassName?: string;
  buttonWrapperClassName?: string;
  buttonClassName?: string;
  showFade?: boolean;
}

export function ExpandableContainer({
  maxLines,
  truncated,
  children,
  isFullWidth = false,
  expandLabel = "Les mer",
  collapseLabel = "Les mindre",
  wrapperClassName,
  contentClassName,
  buttonWrapperClassName,
  buttonClassName,
  showFade = true,
}: ExpandableTextProps) {
  const textRef = useRef<HTMLDivElement>(null);
  const [canTruncate, setCanTruncate] = useState(false);
  const { toggle, aria, isExpanded } = useAccordion(false);

  useEffect(() => {
    const textElement = textRef.current;
    if (!textElement) return;

    setCanTruncate(textElement.scrollHeight > textElement.clientHeight);
  }, [children, maxLines, truncated]);

  return (
    <div className={wrapperClassName}>
      <div
        {...(canTruncate ? aria.panel : {})}
        ref={textRef}
        className={classNames("prose", contentClassName, {
          relative: canTruncate && !isExpanded,
        })}
        style={
          {
            display: isExpanded ? "block" : "-webkit-box",
            WebkitLineClamp:
              isExpanded || typeof maxLines !== "number" ? "unset" : maxLines,
            WebkitBoxOrient: "vertical",
            overflow: isExpanded ? "visible" : "hidden",
            "--cvr-prose-width": isFullWidth ? "100%" : "unset",
            ...(showFade && canTruncate && !isExpanded
              ? {
                  WebkitMaskImage:
                    "linear-gradient(to bottom, black 35%, transparent)",
                  maskImage:
                    "linear-gradient(to bottom, black 35%, transparent)",
                }
              : {}),
          } as React.CSSProperties
        }
      >
        {isExpanded ? children : truncated}
      </div>
      <div
        className={classNames(
          "flex w-full justify-center sm:justify-start",
          buttonWrapperClassName,
        )}
      >
        <Link
          as="button"
          onClick={toggle}
          className={classNames(
            "min-h-fit p-0 text-base font-medium underline underline-offset-4",
            buttonClassName,
            {
              hidden: !canTruncate,
            },
          )}
          {...(canTruncate ? aria.button : {})}
        >
          {isExpanded ? collapseLabel : expandLabel}
        </Link>
      </div>
    </div>
  );
}
